/**
 * Shared attendance kiosk: a tablet mounted at the office door.
 * Paired once by HR (Devices → pairing link, which carries the device secret
 * in the URL fragment). People scan their badge, or type their number, and on a
 * face kiosk then follow a short liveness prompt for a 1:1 match.
 */
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BrandLockup } from '../components/Brand.tsx';
import { Icon } from '../components/Icon.tsx';
import { EASE } from '../components/motion.tsx';
import { api, ApiError } from '../lib/api.ts';
import { captureJpeg, isFrontal, loadLandmarker, readSignal, STEP_PROMPT, stepDetector, type Step } from '../lib/liveness.ts';

const KEY = 'hearth.kiosk';
/** Camera badge scanning needs BarcodeDetector (Chrome on Android, ChromeOS and macOS; not iPad). */
const canScanQr = typeof window !== 'undefined' && 'BarcodeDetector' in window;
type Status = { device: { id: string; name: string; kind: 'face_kiosk' | 'qr_kiosk' }; office: { name: string; timeZone: string }; matcher: 'mock' | 'http' };
type Identified =
  | { mode: 'recorded'; firstName: string; at: string }
  | { mode: 'face'; firstName: string; employeeId: string; challenge: { id: string; nonce: string; steps: Step[]; expiresAt: string } };
type Phase = { kind: 'idle' } | { kind: 'busy' } | { kind: 'face'; who: Extract<Identified, { mode: 'face' }>; stepIdx: number } | { kind: 'done'; name: string; at: string } | { kind: 'error'; message: string };

function credentials(): string | null {
  const fromHash = /^#([0-9a-f-]{36}\.[\w-]{16,})$/i.exec(location.hash)?.[1];
  try {
    if (fromHash) {
      localStorage.setItem(KEY, fromHash);
      history.replaceState(null, '', location.pathname);
    }
    return localStorage.getItem(KEY);
  } catch {
    return fromHash ?? null;
  }
}

export function Kiosk() {
  const [cred, setCred] = useState(credentials);
  const call = useCallback(<T,>(path: string, body?: Record<string, unknown>) => api<T>(`/api/kiosk${path}`, { method: body ? 'POST' : 'GET', body, headers: { authorization: `Device ${cred}` } }), [cred]);
  const [status, setStatus] = useState<Status | null>(null);
  const [unpaired, setUnpaired] = useState(!cred);
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [number, setNumber] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [camera, setCamera] = useState<'starting' | 'on' | 'off'>('starting');
  const video = useRef<HTMLVideoElement>(null);
  const scanner = useRef<HTMLInputElement>(null);
  const loop = useRef(0);

  // A pairing link opened in an already-open kiosk tab only changes the hash.
  useEffect(() => {
    const onHash = () => {
      const c = credentials();
      if (c) {
        setCred(c);
        setUnpaired(false);
      }
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  useEffect(() => {
    if (!cred) return;
    call<Status>('/status').then(setStatus, (e) => e instanceof ApiError && e.status === 401 && setUnpaired(true));
  }, [call, cred]);
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // One front camera for badge scanning (where BarcodeDetector exists) and the face step.
  useEffect(() => {
    if (!status) return;
    let stream: MediaStream | null = null;
    navigator.mediaDevices
      ?.getUserMedia({ video: { facingMode: 'user', width: { ideal: 960 }, height: { ideal: 720 } }, audio: false })
      .then(async (s) => {
        stream = s;
        if (video.current) {
          video.current.srcObject = s;
          await video.current.play().catch(() => undefined);
        }
        setCamera('on');
      })
      .catch(() => setCamera('off'));
    if (!navigator.mediaDevices) setCamera('off');
    return () => stream?.getTracks().forEach((t) => t.stop());
  }, [status]);

  const reset = useCallback((ms: number) => {
    setTimeout(() => {
      setPhase({ kind: 'idle' });
      setNumber('');
      scanner.current?.focus();
    }, ms);
  }, []);
  const fail = useCallback(
    (e: unknown) => {
      setPhase({ kind: 'error', message: e instanceof ApiError ? e.message : 'Something went wrong. Try again.' });
      reset(4500);
    },
    [reset],
  );

  const runFace = useCallback(
    async (who: Extract<Identified, { mode: 'face' }>) => {
      setPhase({ kind: 'face', who, stepIdx: 0 });
      let lm;
      try {
        lm = await loadLandmarker();
      } catch {
        fail(new ApiError(0, 'model', 'The face model could not load. Scan your badge on a QR kiosk, or ask HR.'));
        return;
      }
      const steps = who.challenge.steps;
      const started = performance.now();
      const passed: { step: Step; passedAt: number }[] = [];
      let idx = 0;
      let detect = stepDetector(steps[0]!);
      let frames = 0;
      let last = -1;
      // The latest frame facing the camera: identity is checked on it, not on the last (turned) one.
      let frontal: { jpeg: string; at: number } | null = null;
      const deadline = Date.parse(who.challenge.expiresAt) - 2000;
      const tick = () => {
        const v = video.current;
        if (Date.now() > deadline) return fail(new ApiError(0, 'timeout', 'That took too long. Start again.'));
        if (!v || v.readyState < 2) return void (loop.current = requestAnimationFrame(tick));
        const ts = performance.now();
        if (ts - last > 40) {
          last = ts;
          const sig = readSignal(lm.detectForVideo(v, ts));
          frames++;
          if (isFrontal(sig) && ts - (frontal?.at ?? -Infinity) > 400) frontal = { jpeg: captureJpeg(v), at: ts };
          if (sig.framed && detect(sig)) {
            passed.push({ step: steps[idx]!, passedAt: Math.round(ts - started) });
            idx++;
            setPhase({ kind: 'face', who, stepIdx: idx });
            if (idx >= steps.length) {
              setPhase({ kind: 'busy' });
              call<{ firstName: string; at: string }>('/verify', { challengeId: who.challenge.id, frame: frontal?.jpeg ?? captureJpeg(v), evidence: { nonce: who.challenge.nonce, steps: passed, frames, durationMs: Math.round(ts - started) } }).then(
                (r) => {
                  setPhase({ kind: 'done', name: r.firstName, at: r.at });
                  reset(4000);
                },
                fail,
              );
              return;
            }
            detect = stepDetector(steps[idx]!);
          }
        }
        loop.current = requestAnimationFrame(tick);
      };
      loop.current = requestAnimationFrame(tick);
    },
    [call, fail, reset],
  );

  const identify = useCallback(
    async (ref: { qr?: string; employeeNo?: string }) => {
      if (phase.kind !== 'idle') return;
      setPhase({ kind: 'busy' });
      try {
        const r = await call<Identified>('/identify', ref);
        if (r.mode === 'recorded') {
          setPhase({ kind: 'done', name: r.firstName, at: r.at });
          reset(4000);
        } else void runFace(r);
      } catch (e) {
        fail(e);
      }
    },
    [call, fail, phase.kind, reset, runFace],
  );

  // Camera badge scanning where the browser supports it (Chrome on Android / ChromeOS).
  useEffect(() => {
    if (phase.kind !== 'idle' || !canScanQr) return;
    const Detector = (window as unknown as { BarcodeDetector: new (o: { formats: string[] }) => { detect: (v: HTMLVideoElement) => Promise<{ rawValue: string }[]> } }).BarcodeDetector;
    const det = new Detector({ formats: ['qr_code'] });
    let stop = false;
    const scan = async () => {
      if (stop) return;
      const v = video.current;
      if (v && v.readyState >= 2) {
        const codes = await det.detect(v).catch(() => []);
        if (codes[0]?.rawValue) return void identify({ qr: codes[0].rawValue });
      }
      setTimeout(scan, 350);
    };
    void scan();
    return () => {
      stop = true;
    };
  }, [identify, phase.kind]);
  useEffect(() => () => cancelAnimationFrame(loop.current), []);

  if (unpaired)
    return (
      <div className="kiosk kiosk--center">
        <BrandLockup className="kiosk__lockup kiosk__lockup--lg" />
        <h1 className="kiosk__title">This tablet isn’t paired yet.</h1>
        <p className="kiosk__lede">In Hearth, open <b>Devices</b>, add a face or QR kiosk for this office, and open its pairing link on this tablet.</p>
      </div>
    );

  const tz = status?.office.timeZone ?? 'Asia/Makassar';
  const time = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(now);
  const seconds = new Intl.DateTimeFormat('en-GB', { second: '2-digit', timeZone: tz }).format(now).padStart(2, '0');
  const day = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: tz }).format(now);
  const face = status?.device.kind === 'face_kiosk';
  const steps = phase.kind === 'face' ? phase.who.challenge.steps.length : 0;
  const tag =
    camera === 'off'
      ? 'Camera unavailable'
      : phase.kind === 'face'
        ? `Step ${Math.min(phase.stepIdx + 1, steps)} of ${steps}`
        : phase.kind === 'busy'
          ? 'Checking…'
          : phase.kind === 'done'
            ? 'Recorded'
            : phase.kind === 'error'
              ? 'Not recorded'
              : canScanQr
                ? 'Hold your badge up to the camera'
                : 'Camera ready';

  return (
    <div className="kiosk" onClick={() => phase.kind === 'idle' && scanner.current?.focus()}>
      <header className="kiosk__head">
        <div className="kiosk__brand">
          <BrandLockup className="kiosk__lockup" />
          {status ? (
            <div className="kiosk__where">
              <b>{status.office.name}</b>
              <span>{status.device.name}</span>
            </div>
          ) : null}
          {status?.matcher === 'mock' ? <span className="chip chip--warn">Development matcher</span> : null}
        </div>
        <div className="kiosk__clock">
          <div className="kiosk__time num">
            {time}
            <span className="kiosk__sec">{seconds}</span>
          </div>
          <div className="kiosk__day">{day}</div>
        </div>
      </header>
      <main className="kiosk__body">
        <section className={`kiosk__stage is-${phase.kind}`} aria-hidden="true">
          <video ref={video} playsInline muted />
          <div className="kiosk__shade" />
          {camera === 'off' ? (
            <div className="kiosk__nocam">
              <Icon name="camera" width={36} height={36} />
              <span>Allow camera access in the browser’s site settings.</span>
            </div>
          ) : phase.kind === 'idle' ? (
            <div className="kiosk__frame">
              <svg viewBox="0 0 100 100">
                <path d="M3 24V11a8 8 0 0 1 8-8h13M76 3h13a8 8 0 0 1 8 8v13M97 76v13a8 8 0 0 1-8 8H76M24 97H11a8 8 0 0 1-8-8V76" />
              </svg>
              {canScanQr ? <span className="kiosk__scanline" /> : null}
            </div>
          ) : phase.kind === 'face' ? (
            <div className="kiosk__oval">
              {/* Same 3:4 shape as the box, so strokes scale evenly; the arc starts at the top. */}
              <svg viewBox="0 0 75 100">
                <ellipse className="kiosk__oval-track" cx="37.5" cy="50" rx="37" ry="49.5" />
                <path className="kiosk__oval-done" d="M37.5 0.5A37 49.5 0 1 1 37.5 99.5A37 49.5 0 1 1 37.5 0.5" pathLength={100} strokeDasharray={`${(phase.stepIdx / steps) * 100} 100`} />
              </svg>
            </div>
          ) : phase.kind === 'busy' ? (
            <span className="kiosk__spinner" />
          ) : null}
          <AnimatePresence>
            {phase.kind === 'done' || phase.kind === 'error' ? (
              <motion.div key={phase.kind} className={`kiosk__result kiosk__result--${phase.kind}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25, ease: EASE }}>
                <motion.span className="kiosk__badge" initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 380, damping: 22 }}>
                  <Icon name={phase.kind === 'done' ? 'check' : 'alert'} width={52} height={52} />
                </motion.span>
              </motion.div>
            ) : null}
          </AnimatePresence>
          <div className="kiosk__tag">
            <span className={`kiosk__led is-${camera}`} />
            {tag}
          </div>
        </section>
        <section className="kiosk__panel">
          <AnimatePresence mode="wait">
            <motion.div key={phase.kind + (phase.kind === 'face' ? phase.stepIdx : '')} className="kiosk__state" initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.3, ease: EASE }} aria-live="polite">
              {phase.kind === 'idle' ? (
                <>
                  <h1 className="kiosk__title">{face ? 'Scan your badge to check in.' : 'Scan your badge.'}</h1>
                  <p className="kiosk__lede">{face ? 'Or type your employee number, then look at the camera.' : 'Hold the QR code on your badge up to the camera or the scanner.'}</p>
                  {face ? <Keypad value={number} onChange={setNumber} onSubmit={() => number && void identify({ employeeNo: number })} /> : null}
                </>
              ) : phase.kind === 'busy' ? (
                <h1 className="kiosk__title kiosk__title--soft">One moment…</h1>
              ) : phase.kind === 'face' ? (
                <>
                  <p className="kiosk__hello">Hi {phase.who.firstName}.</p>
                  <h1 className="kiosk__title kiosk__title--xl">{phase.stepIdx < steps ? STEP_PROMPT[phase.who.challenge.steps[phase.stepIdx]!] : 'Hold still…'}</h1>
                  <div className="kiosk__dots" aria-label={`Step ${Math.min(phase.stepIdx + 1, steps)} of ${steps}`}>
                    {phase.who.challenge.steps.map((s, i) => <span key={s} className={i < phase.stepIdx ? 'is-done' : i === phase.stepIdx ? 'is-now' : ''} />)}
                  </div>
                </>
              ) : phase.kind === 'done' ? (
                <>
                  <p className="kiosk__hello kiosk__hello--ok">Checked in</p>
                  <h1 className="kiosk__title kiosk__title--xl">Thanks, {phase.name}.</h1>
                  <p className="kiosk__lede">Recorded at {new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: tz }).format(new Date(phase.at))}.</p>
                </>
              ) : (
                <>
                  <p className="kiosk__hello kiosk__hello--bad">Not recorded</p>
                  <h1 className="kiosk__title">{phase.message}</h1>
                </>
              )}
            </motion.div>
          </AnimatePresence>
        </section>
      </main>
      {/* USB/Bluetooth badge scanners type the code and press Enter. */}
      <input
        ref={scanner}
        className="kiosk__scanner"
        aria-label="Badge scanner input"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const v = e.currentTarget.value.trim();
            e.currentTarget.value = '';
            if (v) void identify({ qr: v });
          }
        }}
      />
    </div>
  );
}

function Keypad({ value, onChange, onSubmit }: { value: string; onChange: (v: string) => void; onSubmit: () => void }) {
  const press = (k: string) => onChange(k === 'back' ? value.slice(0, -1) : k === 'clear' ? '' : `${value}${k}`.slice(0, 12));
  return (
    <div className="kiosk__pad">
      <div className={`kiosk__entry${value ? '' : ' is-empty'}`} aria-live="polite">
        {value ? (
          <>
            <span className="num">{value}</span>
            <span className="kiosk__caret" />
          </>
        ) : (
          'Employee number'
        )}
      </div>
      <div className="kiosk__keys">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'].map((k) => (
          <button key={k} type="button" className={`kiosk__key${k.length > 1 ? ' kiosk__key--fn' : ''}`} aria-label={k === 'back' ? 'Delete' : k === 'clear' ? 'Clear' : undefined} onClick={(e) => { e.stopPropagation(); press(k); }}>
            {k === 'back' ? (
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7Z" />
                <path d="m12.5 9.5 5 5m0-5-5 5" />
              </svg>
            ) : k === 'clear' ? (
              'Clear'
            ) : (
              k
            )}
          </button>
        ))}
      </div>
      <button type="button" className="kiosk__go" disabled={!value} onClick={(e) => { e.stopPropagation(); onSubmit(); }}>
        Continue
        <Icon name="arrowRight" width={20} height={20} />
      </button>
    </div>
  );
}
